// Reception visitor list — RON-5469. Prototype region: `.visitor-list` / `#list-body`.
//
// Columns follow the prototype's `.list-head`: Visitor · Host · Time · Status · Site.
//
// Searching and filtering are SERVER-SIDE (§3), so this component filters nothing — it renders
// exactly the rows it is handed. Same reasoning as the visitor list: once the board is paged, a
// local filter would search only the rows already fetched and tell a receptionist somebody is not
// expected when they are on page two.

import { useTranslation } from "react-i18next";

import {
  ROW_STATUS,
  abbreviateName,
  getRowAvatarTone,
  getInitials,
  getRowTone,
  formatOverstay,
  getStatusLabelKey,
  pluralText,
} from "../receptionConstants.js";
import ReceptionRowMenu from "./ReceptionRowMenu.jsx";

// The pill a row earns. Overstay wins over everything because it is the only one that needs
// acting on; VIP wins over the plain status because it is what the desk needs to see first.
// The status pill. Reads as a single uppercase phrase, the way the prototype has it — a desk
// scans this column down the page rather than reading it, and uppercase with a leading mark is
// what makes the states tell apart at a glance.
const RowPill = ({ row }) => {
  const { t } = useTranslation("common");
  const tone = getRowTone(row);

  if (tone === "overstay") {
    return (
      <span className="reception-pill reception-pill--overstay">
        <span className="reception-pill__mark" aria-hidden="true">
          ⚠
        </span>
        {/* `overstay_by` is minutes past `expected_out`, computed server-side so every device
            agrees. Rendered as a DURATION ("2H 18M") rather than a count of minutes: "138
            minutes" has to be converted in your head before the part that matters — that they
            are over two hours late out — lands. */}
        {t("react_reception_overstay")}
        {row.overstayBy != null ? ` ${formatOverstay(row.overstayBy)}` : ""}
      </span>
    );
  }

  return (
    <span className={`reception-pill reception-pill--${tone}`}>
      {/* A dot for somebody in the building, a star for a VIP. Decorative — the words beside them
          already carry the meaning. */}
      {tone === "vip" && (
        <span className="reception-pill__mark" aria-hidden="true">
          ★
        </span>
      )}
      {tone === "onsite" && <span className="reception-pill__dot" aria-hidden="true" />}

      {/* A walk-in is named in the STATUS, not beside the visitor: it describes how this visit
          came about, which is the same kind of fact as "checked in". Keeping it by the name made
          two badges compete there while this column stayed bare. */}
      {row.isWalkIn ? `${t("react_reception_walk_in")} · ` : ""}
      {t(getStatusLabelKey(row.status))}
    </span>
  );
};

const ReceptionList = ({
  rows,
  isLoading,
  isBusy,
  onCheckIn,
  onCheckOut,
  onNotifyHost,
  onToggleVip,
  onUnavailable,
  isLoadingMore,
  hasMore,
  onLoadMore,
  query,
  selectedId,
  onSelect,
  siteName,
  total,
}) => {
  const { t } = useTranslation("common");

  const renderBody = () => {
    if (isLoading) {
      return <div className="reception-list__state">{t("react_loading")}</div>;
    }

    if (!rows.length) {
      return (
        <div className="reception-list__state">
          {/* Two different facts. "Nothing matches this search" must never read as "nobody is
              expected today" — on a reception desk that is a statement about the building. The
              search term is what tells them apart, since the filtering is server-side. */}
          {String(query || "").trim() === ""
            ? t("react_reception_empty_day")
            : t("react_reception_empty_search")}
        </div>
      );
    }

    return rows.map((row) => {
      const isSelected = row.id === selectedId;

      return (
        // The row is a WRAPPER, and the clickable area is a button inside it. The action menu is
        // a button too, and a button inside a button is invalid HTML that browsers resolve by
        // dropping one of them — so the two are siblings, sharing the row's grid.
        <div
          key={row.id}
          className={`reception-row${isSelected ? " reception-row--selected" : ""}`}
        >
        <button
          type="button"
          className="reception-row__select"
          onClick={() => onSelect?.(row)}
          aria-current={isSelected}
        >
          <span className="reception-row__who">
            <span
              className={`reception-avatar reception-avatar--${getRowAvatarTone(row)}${
                row.isVip ? " reception-avatar--vip" : ""
              }`}
            >
              {getInitials(row.name)}
            </span>
            <span className="reception-row__who-text">
              <span className="reception-row__name">
                {row.name}
                {row.isVip && <span className="reception-tag reception-tag--vip">VIP</span>}
              </span>
              <span className="reception-row__meta">
                {[
                  // A walk-in rarely has a company, and saying so here is more use than a
                  // blank: it explains why the rest of the row is sparse.
                  row.company || (row.isWalkIn ? t("react_reception_walk_in") : null),
                  t(
                    row.kind === "contractor"
                      ? "react_reception_kind_contractor"
                      : "react_reception_kind_visitor"
                  ),
                  // One contractor visit can cover a crew, and the muster counts people rather
                  // than rows — so a party of more than one says so here.
                  row.personnel > 1
                    ? pluralText(t, "react_reception_personnel", row.personnel)
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </span>

          <span className="reception-row__cell">
            {/* Abbreviated: the column is narrow, and the surname is what a receptionist matches
                on. The full name is on the detail panel, where there is room for it. */}
            <span className="reception-row__strong">{abbreviateName(row.host) || "—"}</span>
          </span>

          <span className="reception-row__cell">
            {/* What actually happened beats what was planned: once somebody is in the building,
                the arrival time on the booking is no longer the interesting number.

                The TIME is emphasised and the direction is not — the column is scanned for times,
                and "· in" is the qualifier on it rather than part of what you are looking for. */}
            {row.status === ROW_STATUS.CHECKED_OUT && row.checkedOutAt ? (
              <>
                <span className="reception-row__strong">{row.checkedOutAt}</span>
                {` · ${t("react_reception_out")}`}
              </>
            ) : row.status === ROW_STATUS.CHECKED_IN && row.checkedInAt ? (
              <>
                <span className="reception-row__strong">{row.checkedInAt}</span>
                {` · ${t("react_reception_in")}`}
              </>
            ) : (
              <span className="reception-row__strong">{row.arrivalTime || "—"}</span>
            )}
          </span>

          <span className="reception-row__cell">
            <RowPill row={row} />
          </span>

          <span className="reception-row__cell reception-row__cell--muted">{siteName}</span>
        </button>

          <ReceptionRowMenu
            row={row}
            isBusy={isBusy}
            onView={onSelect}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
            onNotifyHost={onNotifyHost}
            onToggleVip={onToggleVip}
            onUnavailable={onUnavailable}
          />
        </div>
      );
    });
  };

  return (
    <div className="reception-list">
      {/* The header mirrors the row’s two-part shape — five labelled columns plus the action
          column — so the two grids stay aligned by construction rather than by matching numbers
          in two places. */}
      <div className="reception-list__head">
        <span className="reception-list__head-cells">
          <span>{t("react_reception_col_visitor")}</span>
          <span>{t("react_reception_col_host")}</span>
          <span>{t("react_reception_col_time")}</span>
          <span>{t("react_reception_col_status")}</span>
          <span>{t("react_reception_col_site")}</span>
        </span>
        <span aria-hidden="true" />
      </div>

      <div className="reception-list__body">{renderBody()}</div>

      {/* `has_more` is the loop condition, not `truncated` — that key is deprecated and now makes
          a weaker claim than it used to. A board that stops silently is the muster problem the
          backend paged this endpoint for. */}
      {hasMore && (
        <div className="reception-list__more">
          {isLoadingMore ? (
            <span role="status">{t("react_loading")}</span>
          ) : (
            <button
              type="button"
              className="reception-btn reception-btn--ghost"
              onClick={onLoadMore}
            >
              {t("react_reception_load_more")}
            </button>
          )}
        </div>
      )}

      {!hasMore && !isLoading && rows.length > 0 && (
        <p className="reception-list__count">
          {pluralText(t, "react_reception_showing_all", total)}
        </p>
      )}
    </div>
  );
};

export default ReceptionList;
